import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import { OnlyRequiredFields } from "@mapmycustomers/shared";
import { EntityType } from "@mapmycustomers/shared";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import {
  EntityTypesSupportingBoundaryLegends,
  MapEntity,
  Territory,
} from "@mapmycustomers/shared/types/entity";
import { BusinessRule } from "@mapmycustomers/shared/types/entity/BusinessRule";
import { CategorizedMapEntries } from "@mapmycustomers/shared/types/map";
import BoundaryLegend from "@mapmycustomers/shared/types/map/BoundaryLegend";
import { GeoMultiPolygon, GeoPolygon } from "@mapmycustomers/shared/types/shapes";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import SmartTerritoryConfig from "@mapmycustomers/shared/types/territory/SmartTerritoryConfig";
import User from "@mapmycustomers/shared/types/User";
import CombineOperator from "@mapmycustomers/shared/types/viewModel/internalModel/CombineOperator";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import DataViewEntityTypeCount from "@app/component/DataView/type/DataViewEntityTypeCount";
import DownloadEntitiesMapping from "@app/types/DownloadEntitiesMapping";
import { MapFetcherPayload } from "@app/types/map/MapFetcher";
import MapViewportState from "@app/types/map/MapViewportState";
import TerritoryModeState from "@app/types/map/TerritoryModeState";

import TerritoryRegion from "../../components/TerritoriesMode/types/TerritoryRegion";

export const enterTerritoryMode = createAction("map/territory/enter")<{
  areaType?: AreaType; // used when creating smart territory
  territory?: Territory; // useful to specify if you do have territory already, helps UI to update faster
  territoryId: "create" | Territory["id"];
}>();

export const exitTerritoryMode = createAsyncAction(
  "map/territory/exit/request",
  "map/territory/exit/success",
  "map/territory/exit/failure"
)<true | void, void, void>(); // true when we wanna force-exit, usually after leave was approved by user

export const initializeTerritoryMode = createAsyncAction(
  "map/territory/initialize/request",
  "map/territory/initialize/success",
  "map/territory/initialize/failure"
)<
  {
    callback?: (bounds: google.maps.LatLngBounds) => void;
    territoryId?: Territory["id"];
    viewport: MapViewportState;
  },
  {
    businessRules?: BusinessRule[];
    territory?: Territory;
  },
  void
>();

export const applyTerritoryMapViewSettings = createAction("map/territory/applyMapViewSettings")<
  Partial<MapViewState>
>();

export const fetchTerritoryPins = createAsyncAction(
  "map/territory/fetchPins/request",
  "map/territory/fetchPins/success",
  "map/territory/fetchPins/failure"
)<
  MapFetcherPayload,
  CategorizedMapEntries & {
    externalEntries: CategorizedMapEntries;
    pinsCount: number;
  },
  unknown
>();

export const fetchTerritoryRecords = createAsyncAction(
  "map/territory/fetchRecords/request",
  "map/territory/fetchRecords/success",
  "map/territory/fetchRecords/failure"
)<
  {
    selectedBusinessRule?: BusinessRule;
    viewState?: Partial<Pick<MapViewState, "range" | "search" | "sort">>;
  },
  {
    records: MapEntity[];
    recordsCount: number;
  },
  unknown
>();

export const updateTerritory = createAsyncAction(
  "map/territory/updateTerritory/request",
  "map/territory/updateTerritory/success",
  "map/territory/updateTerritory/failure"
)<
  {
    businessRules?: OnlyRequiredFields<BusinessRule, "filters" | "name" | "territories">[];
    onSuccess?: (updatedTerritory: Territory) => void;
    shape?: GeoMultiPolygon | GeoPolygon;
    territory: Territory;
    userIdsToShareWith?: User["id"][];
  },
  Territory,
  void
>();

export const updateTerritorySharing = createAsyncAction(
  "map/territory/updateTerritorySharing/request",
  "map/territory/updateTerritorySharing/success",
  "map/territory/updateTerritorySharing/failure"
)<
  {
    onSuccess?: (updatedTerritory: Territory) => void;
    territory: Territory;
    userIdsToShareWith: User["id"][];
  },
  Territory,
  void
>();

export const setTerritoryEditFormHasChanges = createAction(
  "map/territory/setEditFormHasChanges"
)<boolean>();

export const countPinsInTerritory = createAsyncAction(
  "map/territory/countPinsInTerritory/request",
  "map/territory/countPinsInTerritory/success",
  "map/territory/countPinsInTerritory/failure"
)<
  TerritoryRegion, // either path for drawn territories or selected regions for smart ones,
  { count: number; total: number },
  void
>();

export const countPinsInTerritoryBusinessRule = createAction(
  "map/territory/countPinsInTerritoryBusinessRule"
)<{
  filters: BusinessRule["filters"];
  successCallback: (count: number) => void;
  territoryRegion: TerritoryRegion;
}>();

export const fetchDataForTerritoryMetrics = createAction(
  "map/territory/fetchDataForTerritoryMetrics"
)<{
  businessRule?: Partial<BusinessRule>;
  entityType: Exclude<EntityTypesSupportingBoundaryLegends, EntityType.ACTIVITY>;
  legend: BoundaryLegend;
  successCallback: (data: number) => void;
  territoryRegion: TerritoryRegion;
}>();

export const setTerritoryMode = createAction("map/territory/setTerritoryMode")<
  TerritoryModeState["mode"]
>();

export const toggleDataViewVisibility = createAction(
  "map/territory/toggleDataViewVisibility"
)<void>();

export const updateDataViewEntityTypeCount = createAsyncAction(
  "map/territory/updateDataViewEntityTypeCount/request",
  "map/territory/updateDataViewEntityTypeCount/success",
  "map/territory/updateDataViewEntityTypeCount/failure"
)<
  void,
  {
    entityTypeCount: DataViewEntityTypeCount;
  },
  void
>();

export const setDataViewData = createAction("map/territory/setDataViewData")<
  LongLat[][] | SmartTerritoryConfig
>();

export const fetchTerritoryLassoSelection = createAsyncAction(
  "map/territory/fetchTerritoryLassoSelection/request",
  "map/territory/fetchTerritoryLassoSelection/success",
  "map/territory/fetchTerritoryLassoSelection/failure"
)<
  MapFetcherPayload,
  {
    records: MapEntity[];
  },
  unknown
>();
export const exitTerritoryLassoMode = createAction("map/territory/exitTerritoryLassoMode")<void>();

export const downloadTerritoryRecords = createAction(
  "map/territory/downloadTerritoryRecords"
)<DownloadEntitiesMapping>();

export const setTerritoryRecords = createAction("map/territory/setTerritoryRecords")<MapEntity[]>();

export const updateTerritoryBusinessRules = createAsyncAction(
  "map/territory/updateTerritoryBusinessRules/request",
  "map/territory/updateTerritoryBusinessRules/success",
  "map/territory/updateTerritoryBusinessRules/failure"
)<
  {
    businessRules: OnlyRequiredFields<BusinessRule, "filters" | "name" | "territories">[];
    territoryId: Territory["id"];
  },
  BusinessRule[],
  void
>();

export const fetchTerritoryBusinessRulesRecords = createAsyncAction(
  "map/territory/fetchTerritoryBusinessRulesRecords/request",
  "map/territory/fetchTerritoryBusinessRulesRecords/success",
  "map/territory/fetchTerritoryBusinessRulesRecords/failure"
)<
  {
    combineOperator?: CombineOperator;
    failureCallback: () => void;
    filters: BusinessRule["filters"];
    query?: string;
    successCallback: (records: MapEntity[], total: number) => void;
    territoryRegion: TerritoryRegion;
  },
  void,
  void
>();

export type TerritoryModeActions = ActionType<
  | typeof applyTerritoryMapViewSettings
  | typeof countPinsInTerritory
  | typeof countPinsInTerritoryBusinessRule
  | typeof downloadTerritoryRecords
  | typeof enterTerritoryMode
  | typeof exitTerritoryLassoMode
  | typeof exitTerritoryMode
  | typeof fetchDataForTerritoryMetrics
  | typeof fetchTerritoryBusinessRulesRecords
  | typeof fetchTerritoryLassoSelection
  | typeof fetchTerritoryPins
  | typeof fetchTerritoryRecords
  | typeof initializeTerritoryMode
  | typeof setDataViewData
  | typeof setTerritoryEditFormHasChanges
  | typeof setTerritoryMode
  | typeof setTerritoryRecords
  | typeof toggleDataViewVisibility
  | typeof updateDataViewEntityTypeCount
  | typeof updateTerritory
  | typeof updateTerritoryBusinessRules
  | typeof updateTerritorySharing
>;
